import React, { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
// redux
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../redux/hooks";
import Table from "components/Table/Table";

import InformationBox from "components/common/Message/InformationBox/InformationBox";
import { ATTACHMENT_LIST_COLS } from "./AttachmentListData";
import {
  IMAGE_UPLOAD_DATAFIELDS,
  DAMPER_IMAGE_UPLOADS,
  NFPA_CLASSIFICATION_DATA_FIELD_ID,
} from "components/common/Forms/Utility/ImageUploadFields";
import ViewFileModal from "components/common/ViewFileModal/ViewFileModal";
import { getMasterDataAttachmentList, onDownloadMultipleAttachment, onViewEquipmentAttachment, setAttachmentList, setAttachmentPageDetails } from "redux/pages/Inspection/InspectionTask/InspectionDetails/Equipment/EquipmentSlice";

interface Props {
  showModal: boolean;
  setShowModal: (state: boolean) => void;
  selectedRows: any;
  selectedItems: any;
}

// Mirrors the Inspection Details attachments dialog. Each option carries both identifiers because
// the two are not interchangeable: Comment, Deficiencies and Impairments are told apart by
// dataFieldId, while the damper images share dataFieldId 10010 (NFPA Classification) and are told
// apart only by fileCategoryId.
interface CategoryOption {
  label: string;
  value: string;
  dataFieldId: number;
  fileCategoryId?: number;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  ...Object.keys(IMAGE_UPLOAD_DATAFIELDS).map((dataFieldId) => ({
    label: IMAGE_UPLOAD_DATAFIELDS[Number(dataFieldId)].fieldLabel,
    value: `field-${dataFieldId}`,
    dataFieldId: Number(dataFieldId),
  })),
  ...DAMPER_IMAGE_UPLOADS.map((config) => ({
    label: config.fieldLabel,
    value: `damper-${config.fileCategoryId}`,
    dataFieldId: NFPA_CLASSIFICATION_DATA_FIELD_ID,
    fileCategoryId: config.fileCategoryId,
  })),
].sort((a, b) => a.label.localeCompare(b.label));

export const AttachmentList: React.FC<Props> = ({
  showModal = false,
  setShowModal = () => { },
  selectedRows = [],
  selectedItems = [],
}) => {

  const attachmentList = useAppSelector((state) => state.equipment.attachmentList);
  const dispatch = useDispatch<any>();

  const [pageIndex] = useState(0)
  const [displayInfoModal, setDisplayInfoModal] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>(CATEGORY_OPTIONS[0]?.value);
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [streamData, setStreamData] = useState<any>("");

  const [selectedAttachments, setSelectedAttachments] = useState<any>([]);
  const [attachmentListdetails] = useState({
    currentLength: 100,
    currentStart: 1,
    totalCount: 500,
  });

  const loadAttachments = useCallback((categoryValue: string, sortColumn: string, sortDirection: string) => {
    const option = CATEGORY_OPTIONS.find((item) => item.value === categoryValue);
    if (!option) return;
    setSelectedAttachments([]);
    dispatch(getMasterDataAttachmentList(
      selectedRows[0]?.uniqueSystemId,
      selectedItems[0]?.EquipmentId,
      setMessage,
      setDisplayInfoModal,
      setShowModal,
      sortColumn,
      sortDirection,
      option.dataFieldId,
      option.fileCategoryId,
      true,
    ));
  }, [dispatch, selectedRows, selectedItems, setShowModal]);

  useEffect(() => {
    loadAttachments(selectedCategory, sortField, sortOrder);
  }, [selectedCategory, sortField, sortOrder, loadAttachments]);

  const onSort = (event: any) => {
    setSortField(event?.sortField ?? "");
    setSortOrder(event?.sortOrder === 1 ? "ASC" : "DESC");
  }

  const handleRowSelect = useCallback((data: any) => {
    setSelectedAttachments([...data]);
  }, []);
  const openViewModal = () => {
    if (selectedAttachments.length < 1) {
      setMessage("Select an Attachment");
      setDisplayInfoModal(true);
      return;
    }
    if (selectedAttachments.length > 1) {
      setMessage("This operation can be performed only one row at a time");
      setDisplayInfoModal(true);
      return;
    }
    dispatch(onViewEquipmentAttachment(
      selectedAttachments[0]?.uniqueInspectionTaskId,
      selectedItems[0]?.EquipmentId,
      selectedAttachments[0]?.attachmentId,
      selectedAttachments[0]?.fileName,
      setMessage,
      setDisplayInfoModal,
      setStreamData,
      setShowViewModal,
    ));
  };
  const onDownload = () => {
    if (!selectedAttachments?.length) {
      setMessage("Select an Attachment");
      setDisplayInfoModal(true);
      return;
    }
    dispatch(onDownloadMultipleAttachment(
      selectedAttachments[0]?.uniqueInspectionTaskId,
      selectedItems[0]?.EquipmentId,
      [selectedAttachments[0]?.attachmentId],
      setMessage,
      setDisplayInfoModal,
    ));
  };

  return (
    <>
      <Dialog
        header="Attachments"
        visible={showModal}
        style={{ width: "50vw" }}
        onHide={() => {
          dispatch(setAttachmentList([]));
          dispatch(setAttachmentPageDetails([]));
          setShowModal(false);
        }}
        className="display-settings personal selectsite"
      >
          {/* Address on the left, category filter at the right end - matching the Inspection
              Details attachments dialog. */}
          <div className="d-flex align-items-center justify-content-between pb-2" style={{ gap: "1.5rem" }}>
            <div className="header mb-0 d-flex align-items-center">
              <span className="title">Address:</span>
              <span className="value">{selectedItems[0]?.Address}</span>
            </div>

            <div className="category-filter d-flex align-items-center">
              <label htmlFor="masterdata-attachment-category" className="filter-label">Category:</label>
              <Dropdown
                inputId="masterdata-attachment-category"
                value={selectedCategory}
                options={CATEGORY_OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setSelectedCategory(e.value)}
              />
            </div>
          </div>

          <Table
            rows={attachmentList}
            cols={ATTACHMENT_LIST_COLS.LIST_COLS}
            dataKeyId="attachmentId"
            title={``}
            pageDetails={attachmentListdetails}
            pageIndex={pageIndex}
            showDownloadIcon={true}
            showSettingIcon={false}
            showHamIcon={false}
            showAddIcon={false}
            showDeleteIcon={false}
            showEditIcon={false}
            searchboxfield={false}
            showroweditor={false}
            onSortData={onSort}
            getSelectedRowData={handleRowSelect}
            // Downloads the selection directly - there is only one action, so no caret or menu.
            onDownloadClick={onDownload}
            hideDownloadCaret={true}
            showViewIcon={true}
            showDislaySettingsIcon={false}
            onViewClick={openViewModal}
            defaultSelectedRows={attachmentList?.length > 0 ? [attachmentList[0]] : []}
            settingListItems={[]}
            singleSelect={true}
          />
        <InformationBox
          showInfoModal={displayInfoModal}
          setShowInfoModal={setDisplayInfoModal}
          message={message}
        />
      </Dialog>
      {showViewModal && streamData &&
        <ViewFileModal
          showModal={showViewModal}
          setStreamData={setStreamData}
          setShowModal={setShowViewModal}
          streamData={streamData}
          fileName={selectedAttachments[0]?.fileName}
        />
      }
    </>
  );
};

export default AttachmentList;
